import React, { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function EventManagement() {
    const [events, setEvents] = useState([]);
    const [newEvent, setNewEvent] = useState({ EventID: null, EventName: '', EventDate: '', Location: '', Enabled: 1 });
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Fetch all events
    const fetchAllEvents = async () => {
        setLoading(true);
        try {
            // Get all events
            const eventsResponse = await fetch(`${API_BASE_URL}/getAllEvents`);
            const events = await eventsResponse.json();
            setEvents(events);
        } catch (error) {
            console.error('Error fetching events:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllEvents();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewEvent(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setNewEvent({ EventID: null, EventName: '', EventDate: '', Location: '', Enabled: 1 });
    };

    const handleAddOrUpdateEvent = async () => {
        if (!newEvent.EventName.trim()) {
            alert('Event name is required!');
            return;
        }

        const method = newEvent.EventID ? 'PUT' : 'POST';
        const endpoint = newEvent.EventID ? `${API_BASE_URL}/updateEvent` : `${API_BASE_URL}/createEvent`;

        const eventToSend = {
            ...newEvent,
            Enabled: newEvent.Enabled ? 1 : 0
        };

        try {
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(eventToSend)
            });

            if (response.ok) {
                closeModal();
                // Refetch all events to get fresh data
                await fetchAllEvents();
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to save event.'}`);
            }
        } catch (error) {
            console.error('Error saving event:', error);
            alert('An error occurred while saving the event.');
        }
    };

    const handleEditEvent = (event) => {
        setNewEvent({
            ...event,
            Enabled: event.Enabled === 1 || event.Enabled === true ? 1 : 0
        });
        openModal();
    };

    const handleDeleteEvent = async (EventID) => {
        const confirmDelete = window.confirm('Are you sure you want to permanently delete this event?');
        if (!confirmDelete) return;
        const reconfirmDelete = window.confirm('!!!WARNING!!! Deleting this event will remove all Competitors, Competitions, Scores / Scoring rules, Teams, Leaderboards, and everything associated with this event! Are you really REALLY sure you want to **permanently** delete this event? You have been warned!');
        if (!reconfirmDelete) return;
        
        try {
            const response = await fetch(`${API_BASE_URL}/deleteEvent?EventID=${EventID}`, { method: 'DELETE' });
            if (response.ok) {
                setEvents(events.filter(event => event.EventID !== EventID));
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to delete event.'}`);
            }
        } catch (error) {
            console.error('Error deleting event:', error);
        }
    };

    const handleSort = (column) => {
        const sortedEvents = [...events].sort((a, b) => {
            if (a[column] < b[column]) return -1;
            if (a[column] > b[column]) return 1;
            return 0;
        });
        setEvents(sortedEvents);
        setSortBy(column);
    };

    return (
        <div className="p-1">
            <button className="btn btn-primary mb-1" onClick={openModal}>Add Event</button>

            {loading ? (
                <p>Loading events...</p>
            ) : !Array.isArray(events) || events.length === 0 ? (
                <p>No events found.</p>
            ) : (
                <div className="data-table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('EventName')}>Event Name</th>
                                <th onClick={() => handleSort('EventDate')}>Date</th>
                                <th onClick={() => handleSort('Location')}>Location</th>
                                <th onClick={() => handleSort('Enabled')}>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map(event => (
                                <tr key={event.EventID} style={{ borderBottom: '2px solid #000' }}>
                                    <td>{event.EventName || 'N/A'}</td>
                                    <td>{event.EventDate || 'N/A'}</td>
                                    <td>{event.Location || 'N/A'}</td>
                                    <td>{event.Enabled ? 'Active' : 'Archived'}</td>
                                    <td>
                                        <button className="btn btn-primary" onClick={() => handleEditEvent(event)}>Edit</button>
                                        <button className="btn btn-danger" onClick={() => handleDeleteEvent(event.EventID)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{newEvent.EventID ? 'Edit Event' : 'Add New Event'}</h3>
                        <input
                            className="form-control"
                            type="text"
                            name="EventName"
                            placeholder="Event Name (required)"
                            value={newEvent.EventName}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            className="form-control"
                            type="date"
                            name="EventDate"
                            value={newEvent.EventDate}
                            onChange={handleInputChange}
                        />
                        <input
                            className="form-control"
                            type="text"
                            name="Location"
                            placeholder="Location"
                            value={newEvent.Location}
                            onChange={handleInputChange}
                        />
                        <label className="form-checkbox">
                            <input
                                type="checkbox"
                                name="Enabled"
                                checked={newEvent.Enabled === 1 || newEvent.Enabled === true}
                                onChange={(e) => setNewEvent(prev => ({
                                    ...prev,
                                    Enabled: e.target.checked ? 1 : 0
                                }))}
                            />
                            Active Event
                        </label>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleAddOrUpdateEvent}>
                                {newEvent.EventID ? 'Update Event' : 'Save Event'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
